import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Paper, Typography } from "@mui/material";
import sh from "bundles/common/utils/sh";
import CreateTimeEntryModal from "./CreateTimeEntryModal";

const TIME_ENTRY_CHANGED_EVENT = "time-entry:changed";

function formatHM(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const start = new Date(d); start.setHours(0, 0, 0, 0);
  if (start.getTime() === today.getTime()) return "Today";
  if (start.getTime() === yesterday.getTime()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
}

export default function TimeEntries({ range, startDate, endDate, refreshKey }) {
  const authenticity_token = useSelector((s) => s.authenticity_token);
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);

  const fetchEntries = useCallback(() => {
    if (range === "custom" && (!startDate || !endDate)) return;
    const params = range === "custom"
      ? { from: startDate, to: endDate }
      : { range };
    // caller in the path keeps config.url distinct so concurrent fetches to this
    // endpoint (e.g. CalendarView) don't cancel each other via the sh interceptor.
    sh.get("/internal/time_entries?caller=entries", { params })
      .then((res) => setEntries(Array.isArray(res.data) ? res.data : []));
  }, [range, startDate, endDate]);

  useEffect(() => { fetchEntries(); }, [fetchEntries, refreshKey]);

  useEffect(() => {
    const handler = () => fetchEntries();
    window.addEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
  }, [fetchEntries]);

  const projectColors = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (e.project?.id && e.project.color) map[e.project.id] = e.project.color;
    });
    return map;
  }, [entries]);

  const grouped = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const dayKey = e.started_at?.slice(0, 10) || "";
      if (!map.has(dayKey)) map.set(dayKey, { iso: e.started_at, items: [] });
      map.get(dayKey).items.push(e);
    });
    return [...map.values()].sort((a, b) => new Date(b.iso) - new Date(a.iso));
  }, [entries]);

  const closeModal = () => setEditingEntry(null);
  const handleSaved = () => fetchEntries();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        border: "1px solid hsl(140 8% 88%)",
        backgroundColor: "#ffffff",
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      }}
    >
      <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 15, color: "hsl(150 30% 12%)", mb: 2 }}>
        Time entries
      </Typography>

      {grouped.length === 0 ? (
        <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 13, color: "hsl(150 10% 46%)", py: 4, textAlign: "center" }}>
          No entries for this range yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {grouped.map((day) => {
            const dayTotal = day.items.reduce((sum, e) => sum + (e.duration_secs || 0), 0);
            return (
              <Box key={day.iso}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
                  <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)" }}>
                    {dayLabel(day.iso)}
                  </Typography>
                  <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 12, color: "hsl(150 10% 46%)" }}>
                    {formatHM(dayTotal)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {day.items.map((e) => (
                    <Box
                      key={e.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setEditingEntry(e)}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          setEditingEntry(e);
                        }
                      }}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 1.5, py: 1.25,
                        borderRadius: "10px",
                        border: "1px solid hsl(140 8% 92%)",
                        backgroundColor: "hsl(140 10% 98%)",
                        cursor: "pointer",
                        transition: "background-color 0.15s ease, border-color 0.15s ease",
                        "&:hover": { backgroundColor: "hsl(140 10% 96%)", borderColor: "hsl(140 8% 86%)" },
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: projectColors[e.project?.id] || e.project?.color, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 13, color: "hsl(150 30% 12%)" }} noWrap>
                          {e.project?.name || "—"}
                          {e.task?.name && (
                            <Box component="span" sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", ml: 0.75 }}>
                              · {e.task.name.replace(/_/g, " ")}
                            </Box>
                          )}
                        </Typography>
                        {e.note && (
                          <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 12, color: "hsl(150 10% 46%)", mt: 0.25 }} noWrap>
                            {e.note}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 12, color: "hsl(150 10% 46%)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {formatTime(e.started_at)} – {formatTime(e.ended_at)}
                      </Typography>
                      <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 13, color: "hsl(150 30% 12%)", minWidth: 56, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {formatHM(e.duration_secs || 0)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <CreateTimeEntryModal
        open={!!editingEntry}
        onClose={closeModal}
        entry={editingEntry}
        authenticityToken={authenticity_token}
        onSaved={handleSaved}
      />
    </Paper>
  );
}
